import { CircularProgress } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import type React from 'react';
import { useContext, useEffect, useRef, useState } from 'react';
import { MediaCardActionMenuContainer } from 'src/components/MediaGrid/ActionMenu';
import { MediaCard, MediaCardCardContent, MediaCardMediaComponent } from 'src/components/MediaGrid/MediaCard';
import { SharedCardContent } from 'src/components/MediaGrid/SharedCardContent';
import MediaPreviewModalWithProxy from 'src/components/MediaPreviewModalWithProxy';
import GalleryHero from 'src/Gallery/GalleryHero';
import MediaRouterComponent from 'src/HomePage/DrawerAndPushedContents/PushedContents/AkomiMediaCardGrid/AkomiMediaCard/MediaRouterComponent';
import { getCollectionTypeNameById, getOwner } from 'src/helpers';
import { cleanPayload } from 'src/helpers/common';
import { SharedLinkContext } from 'src/SharedLinkPage/context';
import { useCollectionSimpleQuery } from '../../hooks/collection';
import SharedLinkActionsPerms from '../SharedLinkActionsPerms';

// ISO → "YYYY-MM-DD" via string slicing. Deliberate: avoids new Date()/date-fns
// for display (which introduce TZ drift and bundle weight for a single label).
const formatIsoDate = (iso: string | undefined | null): string | undefined => {
  if (!iso || typeof iso !== 'string' || iso.length < 10) return undefined;
  return iso.slice(0, 10);
};

interface CollectionItemRaw {
  contentObject?: {
    id: string;
    __typename?: string;
  };
}

// Type expected by getCollectionTypeNameById (from src/helpers.ts)
interface CollectionItemForType {
  contentObject: {
    id: string;
    __typename?: string;
  };
}

interface Owner {
  firstName?: string;
  lastName?: string;
  user?: {
    username: string;
  };
}

interface Collection {
  name?: string;
  owner?: Owner;
  dateAdded?: string;
  items?: {
    totalCount?: number;
    pageInfo: {
      hasNextPage: boolean;
      endCursor?: string;
    };
  };
}

interface CollectionData {
  sharedCollection?: Collection;
}

interface SharedGalleryPageStylesClasses {
  root: string;
  mediaGrid: string;
}

const SharedGalleryPage = (): React.JSX.Element => {
  const { linkInfo } = useContext(SharedLinkContext);
  const [indexOfPreviewId, setIndexOfPreviewId] = useState(-1);
  const bottomDivRef = useRef<HTMLDivElement>(null);
  const classes = styles();

  const queryResult = useCollectionSimpleQuery({
    variables: {
      id: linkInfo.collectionId,
    },
    shared: true,
  });

  // Handle the union type - extract properties that exist on QueryResult
  const data = 'data' in queryResult ? queryResult.data : undefined;
  const loading = 'loading' in queryResult ? queryResult.loading : false;
  const error = 'error' in queryResult ? queryResult.error : undefined;
  // biome-ignore lint/suspicious/noExplicitAny: fetchMore returns ApolloQueryResult with generic data type that varies per query
  const fetchMore = 'fetchMore' in queryResult ? queryResult.fetchMore : () => Promise.resolve({} as any);

  const canFetchMore = (): boolean => {
    const { hasNextPage } = (data as CollectionData)?.sharedCollection?.items?.pageInfo ?? {};
    if (hasNextPage) {
      return true;
    }
    return false;
  };

  const fetchMoreItems = (): void => {
    const endCursor = (data as CollectionData)?.sharedCollection?.items?.pageInfo?.endCursor;
    fetchMore({ variables: { first: 5, after: endCursor } });
  };

  const handleScrollToBottom = (): void => {
    canFetchMore() && fetchMoreItems();
  };

  const bottomDivInView = (entry: IntersectionObserverEntry): void => {
    if (entry.isIntersecting) {
      console.log('i see you');
      handleScrollToBottom();
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: bottomDivInView and bottomDivRef are stable references
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(bottomDivInView);
    });
    if (bottomDivRef.current) {
      observer.observe(bottomDivRef.current);
    }
    return () => {
      if (bottomDivRef.current) {
        observer.unobserve(bottomDivRef.current);
      }
    };
  }, [(data as CollectionData)?.sharedCollection]);

  if (loading)
    return (
      <div>
        <CircularProgress color="secondary" />
      </div>
    );

  if (error) return <p>An error occurred - {error.message}</p>;

  const collection = (data as CollectionData)?.sharedCollection;
  const rawItems: CollectionItemRaw[] = cleanPayload<CollectionItemRaw>(collection).filter(
    (item) => item?.contentObject?.id,
  );
  // Cast to the type expected by getCollectionTypeNameById - we've filtered to ensure contentObject exists
  const items: CollectionItemForType[] = rawItems as CollectionItemForType[];
  const itemIDs = items.map((item) => item.contentObject.id);

  const handleImageClick = (i: number): void => {
    setIndexOfPreviewId(i);
  };

  const handleNext = (): void => {
    setIndexOfPreviewId(indexOfPreviewId + 1);
  };

  const handlePrev = (): void => {
    setIndexOfPreviewId(indexOfPreviewId - 1);
  };

  const galleryName = collection?.name || 'Shared Gallery';
  const totalItems = collection?.items?.totalCount ?? itemIDs.length;
  const sharedByName = getOwner(collection?.owner);
  const addedOn = formatIsoDate(collection?.dateAdded);

  const metaItems = [
    ...(sharedByName ? [{ label: 'Shared by', value: sharedByName }] : []),
    ...(addedOn ? [{ label: 'Added', value: addedOn }] : []),
  ];

  return (
    <div className={classes.root}>
      <GalleryHero name={galleryName} totalItems={totalItems} metaItems={metaItems} />
      <div className={classes.mediaGrid}>
        {itemIDs.map((itemId, itemIdIndex) => (
          <MediaCard key={itemId}>
            <MediaCardMediaComponent>
              <MediaCardActionMenuContainer>
                <SharedLinkActionsPerms itemId={itemId} />
              </MediaCardActionMenuContainer>
              <MediaRouterComponent
                shared={true}
                itemId={itemId}
                setPlayer={() => null}
                onImageClick={() => handleImageClick(itemIdIndex)}
                akomiMediaCardIndex={itemIdIndex}
              />
            </MediaCardMediaComponent>
            <MediaCardCardContent>
              <SharedCardContent itemId={itemId} />
            </MediaCardCardContent>
          </MediaCard>
        ))}
        {/* biome-ignore lint/a11y/useKeyWithClickEvents: intersection observer handles scroll, click is fallback only */}
        {/* biome-ignore lint/a11y/noStaticElementInteractions: intersection observer handles scroll, click is fallback only */}
        <div ref={bottomDivRef} onClick={handleScrollToBottom}></div>
        {indexOfPreviewId >= 0 && (
          <MediaPreviewModalWithProxy
            onNext={indexOfPreviewId < itemIDs.length - 1 ? handleNext : undefined}
            onPrev={indexOfPreviewId > 0 ? handlePrev : undefined}
            itemId={itemIDs[indexOfPreviewId]}
            itemType={getCollectionTypeNameById(itemIDs[indexOfPreviewId], items)}
            isShared={true}
            onClose={() => setIndexOfPreviewId(-1)}
          />
        )}
      </div>
    </div>
  );
};

export default SharedGalleryPage;

const styles = makeStyles<Theme, object, keyof SharedGalleryPageStylesClasses>(() => ({
  root: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  mediaGrid: {
    width: '100%',
    height: '100%',
    marginBottom: '20px',
    padding: '10px',
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'row',
    alignContent: 'center',
    rowGap: '10px',
    gap: '6px',
  },
}));
